/**
 * JSONL (JSON Lines) streaming writer and reader.
 *
 * Writer: Appends one JSON object per line to a file using a persistent fd.
 * Reader: Reads a specific page of lines from a JSONL file without loading
 *         the entire file into memory.
 */

import { openSync, writeSync, closeSync, unlinkSync, mkdirSync, createReadStream } from 'node:fs';
import { join } from 'node:path';
import { tmpdir } from 'node:os';
import { randomUUID } from 'node:crypto';
import { createInterface } from 'node:readline';

const TESTRELIC_TMP_DIR = join(tmpdir(), 'testrelic-data');

export class JsonlFileWriter {
  private readonly filePath: string;
  private readonly fd: number;
  private count = 0;
  private closed = false;

  constructor(prefix: string) {
    mkdirSync(TESTRELIC_TMP_DIR, { recursive: true });
    this.filePath = join(TESTRELIC_TMP_DIR, `${prefix}-${randomUUID().substring(0, 8)}.jsonl`);
    this.fd = openSync(this.filePath, 'w');
  }

  append(obj: unknown): void {
    if (this.closed) return;
    const line = JSON.stringify(obj) + '\n';
    writeSync(this.fd, line);
    this.count++;
  }

  getPath(): string {
    return this.filePath;
  }

  getCount(): number {
    return this.count;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    try {
      closeSync(this.fd);
    } catch {
      // fd may already be closed
    }
  }

  cleanup(): void {
    this.close();
    try {
      unlinkSync(this.filePath);
    } catch {
      // File may already be moved/deleted
    }
  }
}

export async function readJsonlPage<T>(
  filePath: string,
  page: number,
  pageSize: number,
  knownTotal?: number,
): Promise<{ items: T[]; total: number; page: number; pageSize: number; totalPages: number }> {
  const skip = (page - 1) * pageSize;
  const items: T[] = [];
  let lineCount = 0;

  const rl = createInterface({
    input: createReadStream(filePath, { encoding: 'utf-8' }),
    crlfDelay: Infinity,
  });

  for await (const line of rl) {
    if (line.length === 0) continue;
    if (lineCount >= skip && items.length < pageSize) {
      try {
        items.push(JSON.parse(line) as T);
      } catch {
        // Skip malformed lines
      }
    }
    lineCount++;
    if (items.length >= pageSize && knownTotal !== undefined) {
      break;
    }
  }

  const total = knownTotal ?? lineCount;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return { items, total, page, pageSize, totalPages };
}
